'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { normalizePath } from '../../../../_utils';
import type { ClawFileItem } from '../../../../types';

interface UseMediaPreviewOptions {
  activeClawId?: string;
  folderPath: string;
  currentFolderItems: ClawFileItem[];
  previewTargetFilePath?: string | null;
  onPreviewHandled?: () => void;
  /** 命中目标是文件夹时进入该目录（正文里点 `📁` 文件夹链接的场景）。 */
  onEnterFolder?: (path: string) => void;
}

export const useMediaPreview = ({
  activeClawId,
  folderPath,
  currentFolderItems,
  previewTargetFilePath,
  onPreviewHandled,
  onEnterFolder,
}: UseMediaPreviewOptions) => {
  const t = useTranslations();
  const [previewFilePath, setPreviewFilePath] = useState('');

  useEffect(() => {
    setPreviewFilePath('');
  }, [activeClawId, folderPath]);

  useEffect(() => {
    const normalizedTargetPath = normalizePath(previewTargetFilePath ?? '');
    if (!normalizedTargetPath) return;

    if (!normalizedTargetPath.startsWith('media')) {
      setPreviewFilePath(normalizedTargetPath);
      onPreviewHandled?.();
      return;
    }

    if (currentFolderItems.length === 0) return;

    const matchedItem = currentFolderItems.find((item) => normalizePath(item.path) === normalizedTargetPath);
    if (!matchedItem) {
      toast({
        type: 'error',
        message: t('claw.file.fileDeleted'),
      });
      return;
    }

    // 目标是文件夹（正文里点的是 `📁` 链接）则进入该目录，而不是当文件预览。
    if (matchedItem.type === 'folder') {
      onEnterFolder?.(normalizedTargetPath);
      onPreviewHandled?.();
      return;
    }

    setPreviewFilePath(normalizedTargetPath);
    onPreviewHandled?.();
  }, [currentFolderItems, previewTargetFilePath]);

  useEffect(() => {
    if (!previewFilePath) return;
    if (!previewFilePath.startsWith('media')) return;
    const stillExists = currentFolderItems.some((item) => normalizePath(item.path) === previewFilePath);
    if (!stillExists) {
      setPreviewFilePath('');
    }
  }, [currentFolderItems, previewFilePath]);

  const activePreviewFile = useMemo(
    () => currentFolderItems.find((item) => normalizePath(item.path) === previewFilePath) ?? null,
    [currentFolderItems, previewFilePath],
  );

  const openPreview = useCallback((path: string) => {
    setPreviewFilePath(path);
  }, []);

  const closePreview = useCallback(() => {
    setPreviewFilePath('');
  }, []);

  return {
    previewFilePath,
    activePreviewFile,
    openPreview,
    closePreview,
  };
};
